import { Injectable, Signal, computed, inject } from '@angular/core';

import { AnalyticsService } from 'src/app/core/services/analytics';
import { AuditLogService } from 'src/app/core/services/audit-log';
import type { AugmentedGoal } from 'src/app/domain/goals/types/Goal.model';
import type { Observation } from 'src/app/domain/observations/types/observations.types';
import type { Student, StudentNote } from 'src/app/domain/student/types/Student.model';
import { AccountState } from 'src/app/store';
import { AdminGoalsState } from 'src/app/store/admin-goals';
import { AdminObservationsState } from 'src/app/store/admin-observations';
import { AdminStudentsState } from 'src/app/store/admin-students';
import { AdminStudentsService } from 'src/app/store/admin-students/admin-students.service';
import { GovernanceState } from 'src/app/store/governance/governance.state';

import type { StudentDataScope, StudentRemovalMode, StudentScopeKind } from './student-data-scope';

/**
 * {@link StudentDataScope} over every student on the active partner — the
 * Admin scope. A pass-through to `Admin*State`, with one deliberate
 * normalisation (see {@link activeGoals}).
 *
 * @security FERPA - handles student PII
 */
@Injectable({ providedIn: 'root' })
export class TeamStudentDataScope implements StudentDataScope {
  private readonly studentsState = inject(AdminStudentsState);
  private readonly analytics = inject(AnalyticsService);
  private readonly auditLog = inject(AuditLogService);
  private readonly goalsState = inject(AdminGoalsState);
  private readonly observationsState = inject(AdminObservationsState);
  private readonly accountState = inject(AccountState);
  private readonly governanceState = inject(GovernanceState);
  private readonly studentsService = inject(AdminStudentsService);

  readonly kind: StudentScopeKind = 'team';

  /**
   * Admin removal deletes the student record for the whole partner, so the
   * details page routes it through the Delete Student flow rather than
   * Notebook's detach-from-my-caseload.
   */
  readonly removalMode: StudentRemovalMode = 'delete-student';

  /** Administrators own the disability categories. */
  readonly canEditProtectedFields = true;

  // ── Students ───────────────────────────────────────────────────────────

  readonly students: Signal<Student[]> = this.studentsState.data;
  readonly studentNameMap = this.studentsState.nameMap;

  getStudentById(id: string): Student | null {
    return this.studentsState.getStudentById(id);
  }

  updateStudentFields(studentId: string, fields: Partial<Student>): void {
    const changedFields = Object.keys(fields);
    // Recorded once the write succeeds; the store updates the screen first.
    this.studentsState.updateStudentFields(studentId, fields, () => {
      this.analytics.track('Student Updated', { fields_changed: changedFields });
      this.auditLog.recordDistrict({
        area: 'students',
        action: 'update',
        target: { type: 'student', id: studentId },
        changedFields,
      });
    });
  }

  addNote(studentId: string, content: string): StudentNote {
    return this.studentsState.addNote(studentId, content, () => this.analytics.track('Student Note Added'));
  }

  editNote(studentId: string, noteId: string, content: string): void {
    this.studentsState.editNote(studentId, noteId, content);
  }

  removeNote(studentId: string, noteId: string): void {
    this.studentsState.removeNote(studentId, noteId, () => this.analytics.track('Student Note Deleted'));
  }

  updateAccommodations(studentId: string, accommodations: string[]): void {
    this.studentsState.updateAccommodations(studentId, accommodations);
  }

  updateModifications(studentId: string, modifications: string[]): void {
    this.studentsState.updateModifications(studentId, modifications);
  }

  /**
   * Delete the student record for the whole partner. `AdminStudentsService`
   * throws when the callable reports failure and clears local state itself on
   * success, so there is nothing to reconcile here.
   */
  async removeStudent(studentId: string): Promise<void> {
    await this.studentsService.deleteStudent(studentId);
  }

  removeStudentLocally(studentId: string): void {
    this.studentsState.removeStudent(studentId);
  }

  // ── Goals ──────────────────────────────────────────────────────────────

  /**
   * Deliberately NOT `AdminGoalsState.activeGoals` — that signal means
   * `status !== 'completed'`, so it also carries drafts. The port contract is
   * progress-monitoring goals only, which is what `MyGoalsState.activeGoals`
   * means and what every shared widget assumes.
   */
  readonly activeGoals: Signal<AugmentedGoal[]> = computed(() =>
    this.goalsState.data().filter(g => g.status === 'active'),
  );

  getGoalsByStudentId(studentId: string): AugmentedGoal[] {
    return this.goalsState.getGoalsForStudent(studentId);
  }

  // ── Observations ───────────────────────────────────────────────────────

  readonly observations: Signal<Observation[]> = this.observationsState.data;
  readonly recentObservations: Signal<Observation[]> = this.observationsState.recentObservations;

  getObservationsByStudentId(studentId: string): Observation[] {
    return this.observationsState.getObservationsByStudentId(studentId);
  }

  // ── Navigation ─────────────────────────────────────────────────────────

  /**
   * The goal cards in Admin are read-only: the goal, finalize, and
   * progress-report pages read the caseload only. Adding a goal or an
   * observation is available, see {@link canAddGoalsAndObservations}.
   */
  readonly canAuthorGoals = false;

  /**
   * An Academy-only district still reaches Administration, and the Goal Drafter
   * and Collect Data pages need a Tools licence and are writes, which
   * compatibility mode blocks.
   */
  get canAddGoalsAndObservations(): boolean {
    return this.accountState.hasToolsAccess() && !this.governanceState.compatibilityModeActive();
  }

  /** Administrators can delete any goal — the same action as the portal's Goals page. */
  readonly canDeleteGoals = true;

  goalUrl(): string | null {
    return null;
  }

  goalDrafterUrl(studentId: string): string {
    return this.accountState.notebookUrl('goal-drafter', studentId);
  }

  goalCollectUrl(goalId: string): string {
    return this.accountState.notebookUrl('collect', 'goal', goalId);
  }

  progressReportUrl(): string | null {
    return null;
  }

  studentListUrl(): string {
    return this.accountState.adminUrl('students');
  }

  studentTabUrl(studentId: string, tab: string): string {
    return this.accountState.adminUrl('students', studentId, tab);
  }
}
